import { useMemo, useState } from 'react'
import type { AgentOutputProps } from '../types'
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline'

/**
 * N8nWorkflowRenderer — n8n-workflow-builder, n8n-enhanced-workflow-builder.
 *
 * Expects { workflow: {...full n8n JSON...}, nodes_used?: [], deploy_url?,
 * tests?: [], documentation? }. Shows a summary card with node counts,
 * deploy link, and a collapsible raw-JSON viewer + download button.
 */

interface N8nNode {
  id?: string
  name?: string
  type?: string
}

interface N8nWorkflow {
  name?: string
  nodes?: N8nNode[]
  connections?: Record<string, any>
}

interface Payload {
  workflow?: N8nWorkflow
  nodes_used?: string[]
  deploy_url?: string
  tests?: Array<{ name?: string; passed?: boolean }>
  documentation?: string
}

function pick(result: any): Payload | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (src?.workflow && typeof src.workflow === 'object') return src as Payload
  }
  return null
}

export default function N8nWorkflowRenderer(props: AgentOutputProps) {
  const p = useMemo(() => pick(props.result), [props.result])
  const [showJson, setShowJson] = useState(false)

  if (!p || !p.workflow) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No n8n workflow found in the agent result.
      </div>
    )
  }

  const wf = p.workflow
  const nodes = wf.nodes || []
  const nodeTypes = Array.from(new Set(nodes.map((n) => n.type).filter(Boolean))) as string[]
  const connectionCount = Object.values(wf.connections || {}).reduce(
    (acc: number, conns: any) => acc + Object.values(conns || {}).reduce((a: number, list: any) => a + (Array.isArray(list) ? list.length : 0), 0),
    0,
  )

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(wf, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(wf.name || 'n8n-workflow').replace(/[^\w-]+/g, '-')}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      {/* Summary card */}
      <div className="flex items-center gap-4 p-4 border border-border rounded-lg bg-card">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: '#FF6D5A20' }}
        >
          <Square3Stack3DIcon className="w-7 h-7" style={{ color: '#FF6D5A' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">
            {wf.name || 'Untitled n8n Workflow'}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {nodes.length} node{nodes.length === 1 ? '' : 's'}
            {connectionCount > 0 && ` · ${connectionCount} connection${connectionCount === 1 ? '' : 's'}`}
            {nodeTypes.length > 0 && ` · ${nodeTypes.length} integration${nodeTypes.length === 1 ? '' : 's'}`}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadJson}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium border border-border hover:bg-accent text-foreground"
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> JSON
          </button>
          {p.deploy_url && (
            <a
              href={p.deploy_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" /> Open in n8n
            </a>
          )}
        </div>
      </div>

      {/* Node types used */}
      {nodeTypes.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">
            Node types
          </div>
          <div className="flex flex-wrap gap-1.5">
            {nodeTypes.map((t) => (
              <span key={t} className="text-xs font-mono px-2 py-0.5 rounded-full border border-border text-foreground bg-muted/30">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tests */}
      {p.tests && p.tests.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">
            Tests
          </div>
          <div className="space-y-1">
            {p.tests.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span style={{ color: t.passed ? '#10B981' : '#EF4444' }}>
                  {t.passed ? '✓' : '✗'}
                </span>
                <span className="text-foreground">{t.name || `test ${i + 1}`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw JSON */}
      <div>
        <button onClick={() => setShowJson((v) => !v)} className="text-xs underline text-primary">
          {showJson ? 'Hide workflow JSON' : 'View workflow JSON'}
        </button>
        {showJson && (
          <pre className="mt-2 text-xs bg-muted/40 border border-border rounded p-3 overflow-auto max-h-[480px] font-mono">
            {JSON.stringify(wf, null, 2)}
          </pre>
        )}
      </div>

      {/* Documentation */}
      {p.documentation && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">
            Documentation
          </div>
          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
            {p.documentation}
          </div>
        </div>
      )}
    </div>
  )
}
